import React, { useState } from 'react';
import { formatDistanceToNow } from 'date-fns';
import {
    MoreVertical, ExternalLink, Download, Pin, Trash2, Eye, X, Hash, Plus,
    FileText, Sparkles, MessageSquarePlus, Check,
} from 'lucide-react';
import { Card, CardContent } from '../ui/card';
import { Input } from '../ui/input';
import Tip from '../ui/tip';
import {
    DropdownMenu, DropdownMenuContent, DropdownMenuItem,
    DropdownMenuTrigger, DropdownMenuSeparator,
} from '../ui/dropdown-menu';
import { cn } from '../../lib/utils';
import { getFileIcon, getPeek, getMatchSnippet, visibleTags, fileSource, isImageFile } from './fileMeta';

/**
 * A single artifact in the grid.
 *
 * The thumbnail is the file's actual opening, not an icon. The old card spent
 * an entire aspect-square panel on one 32px glyph — hundreds of pixels to say
 * "this is a file", which you already knew from being on the Artifacts page.
 * Showing the first few lines instead means you recognise a document the way
 * you do on a shelf: by its shape and its first sentence.
 *
 * Memoized, and every handler takes the file as an argument from the parent's
 * stable useCallbacks — the previous version rebuilt seven closures per card
 * per render, which made memoizing pointless while content search re-filters on
 * every keystroke.
 */
function FileCard({
    file,
    isSelected,
    isChecked,
    selectionActive,
    query,
    onOpen,
    onPreview,
    onPin,
    onDownload,
    onDelete,
    onRename,
    onUpdateTags,
    onToggleSelect,
    onSendToAdvisor,
    readOnly,
}) {
    const [isRenaming, setIsRenaming] = useState(false);
    const [nameValue, setNameValue] = useState(file.name);
    const [isAddingTag, setIsAddingTag] = useState(false);
    const [newTag, setNewTag] = useState('');

    const tags = visibleTags(file);
    const isAdvisor = fileSource(file) === 'advisor';
    const snippet = query ? getMatchSnippet(file, query) : null;

    const handleSaveRename = () => {
        if (nameValue.trim() && nameValue !== file.name) onRename(file, nameValue);
        setIsRenaming(false);
    };

    const handleAddTag = () => {
        const t = newTag.trim();
        if (t && !(file.tags || []).includes(t)) onUpdateTags(file, [...(file.tags || []), t]);
        setNewTag('');
        setIsAddingTag(false);
    };

    return (
        <Card
            data-testid="file-card"
            style={CARD_STYLE}
            onClick={() => onPreview(file)}
            className={cn(
                'group relative cursor-pointer overflow-hidden rounded-2xl border-white/10 bg-secondary/20 transition-all hover:border-primary/30',
                file.pinned && 'ring-2 ring-accent/30',
                // Which file the panel is showing. Without this the panel looks
                // detached from the list you opened it from.
                isSelected && 'border-primary/50 ring-2 ring-primary/40',
                isChecked && 'border-primary ring-2 ring-primary/60',
            )}
        >
            <CardContent className="p-0">
                <FilePeek file={file} snippet={snippet} />

                {/* Selection checkbox. Hidden until hover, but pinned visible the
                    moment any selection exists — otherwise you're hunting for
                    checkboxes halfway through picking files. */}
                {!readOnly && (
                    <Tip label={isChecked ? `Deselect ${file.name}` : `Select ${file.name}`} side="right">
                        <button
                            onClick={(e) => { e.stopPropagation(); onToggleSelect(file, e.shiftKey); }}
                            aria-pressed={isChecked}
                            data-testid="file-card-select"
                            className={cn(
                                'absolute left-2 top-2 z-10 flex h-5 w-5 items-center justify-center rounded-md border transition-all',
                                isChecked
                                    ? 'border-primary bg-primary text-primary-foreground opacity-100'
                                    : 'border-white/25 bg-black/60 text-transparent hover:border-primary/60',
                                !isChecked && !selectionActive && 'opacity-0 group-hover:opacity-100',
                            )}
                        >
                            <Check className="h-3 w-3" />
                        </button>
                    </Tip>
                )}

                {/* Quick actions — dropdown only on mobile, full cluster on hover */}
                <div className="absolute right-2 top-2 z-10 flex gap-1 opacity-0 transition-opacity focus-within:opacity-100 group-hover:opacity-100 max-md:opacity-100">
                    <Tip label="Download" side="top">
                        <button
                            onClick={(e) => { e.stopPropagation(); onDownload(file); }}
                            className="hidden rounded-lg bg-black/50 p-1.5 transition-colors hover:bg-primary/80 md:block"
                        >
                            <Download className="h-3.5 w-3.5 text-white" />
                        </button>
                    </Tip>
                    {!readOnly && (
                        <Tip label={file.pinned ? 'Unpin' : 'Pin'} side="top">
                            <button
                                onClick={(e) => { e.stopPropagation(); onPin(file); }}
                                className="hidden rounded-lg bg-black/50 p-1.5 transition-colors hover:bg-black/70 md:block"
                            >
                                <Pin className={cn('h-3.5 w-3.5', file.pinned ? 'fill-accent text-accent' : 'text-white')} />
                            </button>
                        </Tip>
                    )}
                    <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                            <button
                                onClick={(e) => e.stopPropagation()}
                                className="rounded-lg bg-black/50 p-1.5 transition-colors hover:bg-black/70"
                                aria-label={`Actions for ${file.name}`}
                            >
                                <MoreVertical className="h-3.5 w-3.5 text-white" />
                            </button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => onOpen(file)}>
                                <ExternalLink className="mr-2 h-4 w-4" /> Open editor
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => onPreview(file)}>
                                <Eye className="mr-2 h-4 w-4" /> Open in panel
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => onDownload(file)}>
                                <Download className="mr-2 h-4 w-4" /> Download
                            </DropdownMenuItem>
                            {!readOnly && (
                                <>
                                    <DropdownMenuItem onClick={() => onSendToAdvisor(file)}>
                                        <MessageSquarePlus className="mr-2 h-4 w-4" /> Send to Advisor
                                    </DropdownMenuItem>
                                    <DropdownMenuSeparator />
                                    <DropdownMenuItem onClick={() => onPin(file)}>
                                        <Pin className={cn('mr-2 h-4 w-4', file.pinned && 'fill-accent text-accent')} />
                                        {file.pinned ? 'Unpin' : 'Pin'}
                                    </DropdownMenuItem>
                                    <DropdownMenuItem onClick={() => setIsRenaming(true)}>
                                        <FileText className="mr-2 h-4 w-4" /> Rename
                                    </DropdownMenuItem>
                                    <DropdownMenuSeparator />
                                    <DropdownMenuItem className="text-red-500" onClick={() => onDelete(file)}>
                                        <Trash2 className="mr-2 h-4 w-4" /> Delete
                                    </DropdownMenuItem>
                                </>
                            )}
                        </DropdownMenuContent>
                    </DropdownMenu>
                </div>

                {/* File info */}
                <div className="border-t border-white/5 p-3">
                    <div className="flex items-center gap-1.5">
                        <span className="flex-shrink-0 [&_svg]:h-3.5 [&_svg]:w-3.5">
                            {getFileIcon(file.type, file.name, 'h-3.5 w-3.5')}
                        </span>
                        {isRenaming ? (
                            <Input
                                value={nameValue}
                                onChange={(e) => setNameValue(e.target.value)}
                                onBlur={handleSaveRename}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter') handleSaveRename();
                                    if (e.key === 'Escape') { setNameValue(file.name); setIsRenaming(false); }
                                }}
                                className="h-6 p-1 text-xs"
                                autoFocus
                                onClick={(e) => e.stopPropagation()}
                            />
                        ) : (
                            <button
                                className={cn(
                                    'min-w-0 flex-1 truncate text-left font-mono text-xs font-medium transition-colors',
                                    readOnly ? 'cursor-pointer' : 'hover:text-primary'
                                )}
                                onClick={(e) => {
                                    if (!readOnly) {
                                        e.stopPropagation();
                                        setIsRenaming(true);
                                    }
                                }}
                                title={readOnly ? file.name : 'Click to rename'}
                            >
                                {file.name}
                            </button>
                        )}
                        {file.pinned && <Pin className="h-3 w-3 flex-shrink-0 fill-accent text-accent" />}
                    </div>

                    {(isAdvisor || tags.length > 0 || !readOnly) && (
                        <div className="mt-2 flex flex-wrap items-center gap-1">
                            {isAdvisor && <AdvisorBadge />}
                            {tags.map((tag) => (
                                <span
                                    key={tag}
                                    className="group/tag inline-flex items-center gap-0.5 rounded-md border border-primary/30 bg-primary/20 px-1.5 py-0.5 text-[9px] text-primary"
                                >
                                    <Hash className="h-2 w-2" />
                                    {tag}
                                    {!readOnly && (
                                        <button
                                            onClick={(e) => { e.stopPropagation(); onUpdateTags(file, (file.tags || []).filter((t) => t !== tag)); }}
                                            className="ml-0.5 opacity-0 transition-opacity hover:text-red-400 group-hover/tag:opacity-100"
                                            aria-label={`Remove tag ${tag}`}
                                        >
                                            <X className="h-2 w-2" />
                                        </button>
                                    )}
                                </span>
                            ))}
                            {isAddingTag ? (
                                <input
                                    type="text"
                                    value={newTag}
                                    onChange={(e) => setNewTag(e.target.value)}
                                    onBlur={handleAddTag}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter') handleAddTag();
                                        if (e.key === 'Escape') { setNewTag(''); setIsAddingTag(false); }
                                    }}
                                    placeholder="tag"
                                    className="w-12 rounded-md border border-primary/30 bg-transparent px-1 py-0.5 text-[9px] outline-none"
                                    autoFocus
                                    onClick={(e) => e.stopPropagation()}
                                />
                            ) : (
                                !readOnly && (
                                    <button
                                        onClick={(e) => { e.stopPropagation(); setIsAddingTag(true); }}
                                        className="rounded-md border border-dashed border-white/20 px-1.5 py-0.5 text-[9px] text-muted-foreground transition-colors hover:border-primary/50 hover:text-primary"
                                        aria-label="Add tag"
                                    >
                                        <Plus className="h-2 w-2" />
                                    </button>
                                )
                            )}
                        </div>
                    )}

                    <div className="mt-1.5 flex items-center justify-between text-[10px] text-muted-foreground">
                        <span className="truncate">{file.category}</span>
                        <span className="flex-shrink-0 tabular-nums">
                            {formatDistanceToNow(new Date(file.last_edited))} ago
                        </span>
                    </div>
                </div>
            </CardContent>
        </Card>
    );
}

// Long grids get expensive fast; skip layout and paint for cards off-screen.
// contain-intrinsic-size is the card's approximate height so the scrollbar
// doesn't jump as they resolve.
const CARD_STYLE = { contentVisibility: 'auto', containIntrinsicSize: '320px' };

/**
 * The thumbnail: an image, the document's opening lines, or the matched line
 * when a content search put this card on screen.
 */
export function FilePeek({ file, snippet, onClick, className }) {
    if (isImageFile(file)) {
        return (
            <div className={cn('relative aspect-square overflow-hidden', className)} onClick={onClick}>
                <img src={file.content} alt={file.name} className="h-full w-full object-cover" loading="lazy" />
            </div>
        );
    }

    if (snippet) {
        return (
            <div
                onClick={onClick}
                className={cn(
                    'relative flex aspect-square items-center overflow-hidden bg-[hsl(240_11%_7%)]/60 p-3',
                    className
                )}
            >
                <p className="max-h-full overflow-hidden break-words font-mono text-[9px] leading-[1.5] text-muted-foreground">
                    {snippet.before}
                    <mark className="rounded-sm bg-primary/25 px-0.5 text-primary">{snippet.match}</mark>
                    {snippet.after}
                </p>
            </div>
        );
    }

    const peek = getPeek(file);

    if (!peek) {
        return (
            <div
                onClick={onClick}
                className={cn(
                    'relative flex aspect-square items-center justify-center bg-gradient-to-br from-secondary/50 to-secondary/20',
                    className
                )}
            >
                <div className="p-4 transition-transform duration-200 md:group-hover:scale-125">
                    {getFileIcon(file.type, file.name)}
                </div>
            </div>
        );
    }

    return (
        <div
            onClick={onClick}
            className={cn(
                'relative aspect-square overflow-hidden bg-[hsl(240_11%_7%)]/60 p-3',
                '[mask-image:linear-gradient(to_bottom,black_55%,transparent_100%)]',
                className
            )}
        >
            <pre className="whitespace-pre-wrap break-words font-mono text-[7px] leading-[1.35] text-muted-foreground/70">
                {peek}
            </pre>
        </div>
    );
}

export function AdvisorBadge({ className }) {
    return (
        <span
            title="Written by the Advisor"
            className={cn(
                'inline-flex items-center gap-0.5 rounded-md border border-accent/30 bg-accent/15 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wide text-accent',
                className
            )}
        >
            <Sparkles className="h-2 w-2" />
            Advisor
        </span>
    );
}

export default React.memo(FileCard);
